/**
 * Handler: begin_checkout event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontCart } from '../../types/storefront';
import { adaptCart } from '../../adapters/cart';
import { generateEventId, gtmMarketFields } from '../utils';
import { trackKlaviyoBeginCheckoutDirect } from '../fallbacks/klaviyo';

export function handleBeginCheckout(
  analyzify: WindowAnalyzify,
  cart: StorefrontCart
): void {
  const adaptedCart = adaptCart(cart);
  const eventId = generateEventId();

  // GA4
  if (typeof analyzify.gaBeginCheckout === 'function') {
    analyzify.gaBeginCheckout(adaptedCart, eventId);
  }

  // Facebook
  if (typeof analyzify.fbBeginCheckout === 'function') {
    analyzify.fbBeginCheckout(adaptedCart, eventId);
  }

  // TikTok
  if (typeof analyzify.ttBeginCheckout === 'function') {
    analyzify.ttBeginCheckout(adaptedCart, eventId);
  }

  // GTM - try CDN first, fall back to direct dataLayer push
  const win = window as any;
  let gtmTracked = false;
  if (typeof analyzify.gtmBeginCheckout === 'function') {
    try {
      analyzify.gtmBeginCheckout(adaptedCart, eventId);
      gtmTracked = true;
    } catch {
      // CDN function failed — fall through to dataLayer push
    }
  }

  if (!gtmTracked && Array.isArray(win.dataLayer) && analyzify.properties?.GTM?.status) {
    const cartItems = adaptedCart.items || [];

    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({
      event: 'ee_begin_checkout',
      ss_event_id: eventId || analyzify.eventId.get('begin_checkout') || null,
      ecommerce: {
        analyzify_source: 'advanced',
        implementation_type: 'extension',
        currency: analyzify.currency || null,
        value: analyzify.formatPrice?.(adaptedCart.total_price, true) ?? adaptedCart.total_price / 100,
        total_quantity: Number(adaptedCart.item_count) || 0,
        total_item: cartItems.length || 0,
        cart_id: adaptedCart.token?.split('?')[0] || analyzify.cart_id || null,
        items: cartItems.map((item: any, index: number) => ({
          id: analyzify.getShopifyId?.(
            analyzify.feed_region || '',
            item.product_id,
            item.variant_id,
            item.sku
          ) || null,
          item_id: item.product_id?.toString() || null,
          item_name: item.product_title?.trim() || item.title?.trim() || null,
          price: analyzify.formatPrice?.(item.price, true) ?? item.price / 100,
          item_category: item.product_type || item.type || null,
          item_brand: item.vendor || null,
          item_barcode: null,
          discount: analyzify.formatPrice?.(item.line_level_total_discount, true) || 0,
          item_handle: item.handle || null,
          item_variant: item.variant_title?.trim() || null,
          item_variant_id: item.variant_id?.toString() || null,
          item_sku: item.sku || null,
          index,
          quantity: item.quantity || 1,
        })),
      },
      ...gtmMarketFields(analyzify),
    });
  }

  // Bing
  if (typeof analyzify.bingBeginCheckout === 'function') {
    analyzify.bingBeginCheckout(adaptedCart);
  }

  // Snapchat
  if (typeof analyzify.snapchatBeginCheckout === 'function') {
    analyzify.snapchatBeginCheckout(adaptedCart);
  }

  // Klaviyo - try CDN first, fall back to direct API
  const klaviyoPropsBC = analyzify.properties?.KLAVIYO;
  if (klaviyoPropsBC?.status && klaviyoPropsBC.events?.begin_checkout) {
    if (typeof analyzify.klaviyoBeginCheckout === 'function') {
      try {
        analyzify.klaviyoBeginCheckout(adaptedCart);
      } catch (error) {
        trackKlaviyoBeginCheckoutDirect(analyzify, adaptedCart);
      }
    } else {
      trackKlaviyoBeginCheckoutDirect(analyzify, adaptedCart);
    }
  }

  // X
  if (typeof analyzify.xBeginCheckout === 'function') {
    analyzify.xBeginCheckout(adaptedCart);
  }

  // Server-side
  if (typeof analyzify.ssBeginCheckout === 'function') {
    analyzify.ssBeginCheckout(adaptedCart, eventId);
  }

  // Clarity
  if (typeof analyzify.clarityBeginCheckout === 'function') {
    analyzify.clarityBeginCheckout();
  }

  // Hotjar
  if (typeof analyzify.hotjarBeginCheckout === 'function') {
    analyzify.hotjarBeginCheckout();
  }
}
